import { Box, Tab, Tabs, Tooltip } from '@mui/material'
import { Page } from 'Common/Page/Page'
import {
  Button,
  DropDown,
  PageHeader,
  CalendarInput,
  TabPanel,
} from 'Common/Supplyvue-ui'
import { AdherenceTable } from './Adherence.table'
import { AdherenceChart } from './AdherenceChart.view'
import { useAdherenceReport } from './AdherenceReport.data'
import { AdherenceReportData } from 'Features/AdherenceReport/AdherenceReport.types'
import { BaseSyntheticEvent, RefObject, useEffect, useState } from 'react'
import { Loader } from 'Common/Loader/Loader'
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined'
import { MultiSelectDropdown } from 'Features/AdherenceReport/MultiSelectDropdown'
import DownloadIcon from '@mui/icons-material/Download'
import { styled } from '@mui/material/styles'
import { GridApiPremium, useGridApiRef } from '@mui/x-data-grid-premium'
import { dateToInt, intToDate } from './AdherenceReport.dateUtils'

const TABS = {
  TABLE: 'table',
  CHART: 'chart',
}

const StyledCalendarInput = styled(CalendarInput)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: 40,
  },
  '& input': {
    height: 40,
    padding: '10px 12px',
    boxSizing: 'border-box',
  },
}))

// "Previous Monday" = the most recent Monday on or before today (today itself,
// if today is a Monday).
const getPreviousMonday = (): Date => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(today)
  monday.setDate(today.getDate() + diff)
  return monday
}

const getDefaultDateRange = (): { start: number; end: number } => {
  const previousMonday = getPreviousMonday()
  const fourWeeksBeforeThat = new Date(previousMonday)
  fourWeeksBeforeThat.setDate(previousMonday.getDate() - 28)

  return {
    start: dateToInt(fourWeeksBeforeThat),
    end: dateToInt(previousMonday),
  }
}

export const AdherenceReportPage = () => {
  const [selectedLag, setSelectedLag] = useState<number>(8)
  const { data: adherenceReportData, isFetching } =
    useAdherenceReport(selectedLag)
  const [tableData, setTableData] = useState<
    AdherenceReportData[] | undefined
  >()
  const [selectedTab, setSelectedTab] = useState(TABS.TABLE)

  const defaultDateRange = getDefaultDateRange()
  const [startDate, setStartDate] = useState<number | undefined>(
    defaultDateRange.start
  )
  const [endDate, setEndDate] = useState<number | undefined>(
    defaultDateRange.end
  )
  const [selectedSiteCodes, setSelectedSiteCodes] = useState<string[] | null>(
    null
  )
  const [selectedSkuCodes, setSelectedSkuCodes] = useState<string[] | null>(
    null
  )
  const [selectedWorkCentreCodes, setSelectedWorkCentreCodes] = useState<
    string[] | null
  >(null)
  const [siteCodes, setSiteCodes] = useState<string[]>([])
  const [skuCodes, setSkuCodes] = useState<string[]>([])
  const [workCentreCodes, setWorkCentreCodes] = useState<string[]>([])
  const apiRef = useGridApiRef()
  const lags: { key: string; value: string }[] = Array.from(
    { length: 51 },
    (_, i) => ({
      key: `Lag ${i}`,
      value: i.toString(),
    })
  )

  useEffect(() => {
    if (adherenceReportData) {
      const siteCodes = [
        ...new Set(
          adherenceReportData.map(
            (currentData: AdherenceReportData) => currentData.siteCode
          )
        ),
      ]

      setSiteCodes(siteCodes)

      const skuCodes = [
        ...new Set(
          adherenceReportData.map(
            (currentData: AdherenceReportData) => currentData.skuCode
          )
        ),
      ]

      setSkuCodes(skuCodes)

      const workCentreCodes = [
        ...new Set(
          adherenceReportData
            .map(
              (currentData: AdherenceReportData) =>
                currentData.primaryWorkCentreCode
            )
            .filter((code): code is string => Boolean(code))
        ),
      ]

      setWorkCentreCodes(workCentreCodes)

      setTableData(adherenceReportData)
    }
  }, [adherenceReportData])

  const handleSiteCodesChange = (input: any) => {
    setSelectedSiteCodes(input)
  }
  const handleSkuCodesChange = (input: any) => {
    setSelectedSkuCodes(input)
  }
  const handleWorkCentreCodesChange = (input: any) => {
    setSelectedWorkCentreCodes(input)
  }

  const handleDropDownClick = (input: BaseSyntheticEvent) => {
    const { name, value } = input.target
    if (name === 'siteCodeDropdown') {
      setSelectedSiteCodes(value)
    } else if (name === 'lagDropdown') {
      setSelectedLag(Number(value))
    } else {
      setSelectedSkuCodes(value)
    }
  }

  const handleClearFilters = () => {
    if (adherenceReportData) {
      setSelectedSkuCodes(null)
      setSelectedSiteCodes(null)
      setSelectedWorkCentreCodes(null)
      setTableData(adherenceReportData)
    }
  }

  useEffect(() => {
    if (adherenceReportData) {
      const selectedSiteCodesSet = selectedSiteCodes
        ? new Set(selectedSiteCodes)
        : null
      const selectedSkuCodesSet = selectedSkuCodes
        ? new Set(selectedSkuCodes)
        : null
      const selectedWorkCentreCodesSet = selectedWorkCentreCodes
        ? new Set(selectedWorkCentreCodes)
        : null
      const newTableData = adherenceReportData.filter(
        (value: AdherenceReportData) => {
          if (selectedLag !== value.lagNumber) {
            return false
          }

          if (startDate && value.dateKey < startDate) {
            return false
          }

          if (endDate && value.dateKey > endDate) {
            return false
          }

          if (
            selectedSiteCodesSet &&
            !selectedSiteCodesSet.has(value.siteCode)
          ) {
            return false
          }

          if (selectedSkuCodesSet && !selectedSkuCodesSet.has(value.skuCode)) {
            return false
          }

          if (
            selectedWorkCentreCodesSet &&
            !selectedWorkCentreCodesSet.has(value.primaryWorkCentreCode)
          ) {
            return false
          }

          return true
        }
      )

      setTableData(newTableData)
    }
  }, [
    selectedSiteCodes,
    selectedSkuCodes,
    selectedWorkCentreCodes,
    startDate,
    endDate,
    adherenceReportData,
  ])

  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const onDownload = () => {
    setIsDownloading(true)
    apiRef?.current?.exportDataAsExcel()
    setIsDownloading(false)
  }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Adherence Report" />
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              marginBottom: '20px',
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
              sx={{
                marginLeft: '4px',
                height: '40px',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
            >
              <Tooltip title={'Download Report'}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isDownloading}
                  onClick={onDownload}
                  startIcon={
                    <DownloadIcon
                      sx={(theme) => ({
                        color: '#00415A',
                      })}
                    />
                  }
                >
                  Download
                </Button>
              </Tooltip>
            </Box>

            <Box display="flex" gap="8px" alignItems="center">
              <Box display="flex" sx={{ width: '185px', height: '40px' }}>
                <StyledCalendarInput
                  onChange={(value: Date | null) => {
                    if (value) {
                      setStartDate(dateToInt(value))
                    }
                  }}
                  shouldDisableDate={(date) => date.getDay() !== 1}
                  format="dd-MM-yyyy"
                  value={startDate ? intToDate(startDate) : new Date()}
                />
              </Box>
              <Box display="flex" sx={{ width: '185px', height: '40px' }}>
                <StyledCalendarInput
                  onChange={(value: Date | null) => {
                    if (value) {
                      setEndDate(dateToInt(value))
                    }
                  }}
                  shouldDisableDate={(date) => date.getDay() !== 1}
                  format="dd-MM-yyyy"
                  value={endDate ? intToDate(endDate) : new Date()}
                />
              </Box>
              <Box display="flex" sx={{ width: '150px', height: '40px' }}>
                <DropDown
                  menuItems={lags ?? []}
                  name="lagDropdown"
                  value={selectedLag}
                  onChange={handleDropDownClick}
                  label={'Lag'}
                  defaultLabel={'Select Lag'}
                  fullWidth={true}
                />
              </Box>
              <Box display="flex" sx={{ width: '150px', height: '40px' }}>
                <MultiSelectDropdown
                  data={siteCodes ?? []}
                  label={'Site Codes'}
                  selectedValues={selectedSiteCodes ?? siteCodes}
                  onChange={handleSiteCodesChange}
                />
              </Box>
              <Box display="flex" sx={{ width: '150px', height: '40px' }}>
                <MultiSelectDropdown
                  data={skuCodes}
                  label={'Sku Codes'}
                  selectedValues={selectedSkuCodes ?? skuCodes}
                  onChange={handleSkuCodesChange}
                />
              </Box>
              <Box display="flex" sx={{ width: '150px', height: '40px' }}>
                <MultiSelectDropdown
                  data={workCentreCodes}
                  label={'Work Centre'}
                  selectedValues={selectedWorkCentreCodes ?? workCentreCodes}
                  onChange={handleWorkCentreCodesChange}
                />
              </Box>
              <Box display="flex" sx={{}}>
                <Tooltip title={'Clear Filters'}>
                  <BackspaceOutlinedIcon
                    onClick={handleClearFilters}
                    sx={{
                      color: 'lightgrey',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'grey',
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
          {tableData ? (
            <>
              <Box sx={{ marginBottom: '12px' }}>
                <Tabs
                  value={selectedTab}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={(_event, newValue) => setSelectedTab(newValue)}
                >
                  <Tab label="Table" value={TABS.TABLE} />
                  <Tab
                    label="Stock Policy Adherence Chart"
                    value={TABS.CHART}
                  />
                </Tabs>
              </Box>
              <TabPanel value={selectedTab} index={TABS.TABLE}>
                <AdherenceTable
                  data={tableData}
                  isLoading={isFetching}
                  apiRef={apiRef as RefObject<GridApiPremium>}
                />
              </TabPanel>
              <TabPanel value={selectedTab} index={TABS.CHART}>
                <AdherenceChart
                  data={tableData}
                  startDate={startDate}
                  endDate={endDate}
                />
              </TabPanel>
            </>
          ) : (
            <Loader />
          )}
        </Box>
      </Box>
    </Page>
  )
}
